import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { customersService } from '@/api/services/customers'
import type { Customer, CustomerPayload } from '@/api/types'
import { useCookie } from '@/composables/useCookie'
import { useFormFields } from '@/composables/useFormFields'
import { useRequest } from '@/composables/useRequest'
import { LOGGED_OUT_COOKIE, USER_ID_COOKIE } from '@/constants/storage'
import { setCookie } from '@/utils/cookie'
import { resetLocale } from '@/i18n'
import { secureUrl } from '@/utils/product'
import { identify } from '@/utils/track'

/**
 * Профиль клиента. `user` — computed из полей формы и данных бэка: он же
 * служит телом для Create/Update, поэтому форма и payload всегда синхронны
 * (перенос `store/user.ts`).
 */
export const useUserStore = defineStore('user', () => {
  // Cookie, а не localStorage: её несёт с собой каждый запрос к прокси,
  // и остальное состояние (регион, ресторан, координаты) тоже живёт в cookie
  const userId = useCookie(USER_ID_COOKIE)

  const {
    requiredField: firstName,
    isRequiredCorrect: isFirstNameCorrect,
    code,
    phone: userPhone,
    isPhoneCorrect,
    phoneNumber: userPhoneNumber,
  } = useFormFields()

  /* Пусто, пока бэкенд не отдал дату: иначе в профиле светился бы сегодняшний день */
  const birthDate = ref('')

  /* Фамилия и почта у бэкенда есть — форма их правит наравне с именем */
  const lastName = ref('')
  const email = ref('')

  /* Пол: null, пока клиент его не выбрал — нуля в enum `Gender` нет */
  const gender = ref<number | null>(null)

  /* Выбранное, но ещё не сохранённое фото — base64 без префикса `data:` */
  const photo = ref('')

  /** Дата обязательна при регистрации — подставляем сегодняшнюю только в payload. */
  const todayIso = (): string => new Date().toISOString().split('T')[0] ?? ''
  const profile = ref<Customer | null>(null)

  /** Курс бонусов: 1 балл = 1 сум. Придёт из настроек, когда подключим /system. */
  const scoreRate = 1

  const isAuthorized = computed(() => Boolean(userId.value))

  /**
   * Карточка клиента доехала. До этого справочник адресов пуст не потому,
   * что адресов нет, а потому, что их ещё не привезли, — и судить по нему
   * нельзя.
   */
  const isProfileLoaded = computed(() => profile.value !== null)

  /**
   * Справочник адресов приезжает вместе с карточкой клиента — отдельного
   * запроса за ним нет. Стор локации слушает его и держит свой список.
   */
  const addressList = computed(() => profile.value?.addressList ?? [])

  const user = computed<CustomerPayload>(() => ({
    id: userId.value,
    firstName: firstName.value,
    lastName: lastName.value,
    email: email.value,
    phone1: userPhoneNumber.value,
    birthDate: birthDate.value || todayIso(),
    cardBalance: profile.value?.cardBalance ?? 0,
    cardNumber: profile.value?.cardNumber ?? 0,
    /*
     * В payload поле зовётся `gender`, а в ответе GET — `genderId`.
     * Пустое не шлём: Update правит только пришедшие поля, и `null` он
     * пропускает мимо, а не сбрасывает (проверено на живом сервисе).
     */
    ...(gender.value ? { gender: gender.value } : {}),
    ...(photo.value ? { photoBase64: photo.value } : {}),
  }))

  /**
   * Аватар для экранов: пока фото не сохранено — показываем локальный превью,
   * дальше ссылку от бэкенда. Она приходит по http, а страница в Telegram
   * всегда https — иначе картинку заблокирует mixed content.
   *
   * Пусто, если фото нет: тогда экраны показывают рисованный аватар.
   */
  const avatarUrl = computed(() =>
    photo.value ? `data:image/jpeg;base64,${photo.value}` : secureUrl(profile.value?.imgUrl ?? ''),
  )

  function setUserId(id: string): void {
    userId.value = id
    /* Теперь ошибки и шаги этой сессии привязаны к клиенту, а не к «кому-то» */
    identify(id)
  }

  const getRequest = useRequest(customersService.getById)
  const createRequest = useRequest(customersService.create)
  const updateRequest = useRequest(customersService.update)

  const isLoading = computed(
    () =>
      getRequest.isLoading.value || createRequest.isLoading.value || updateRequest.isLoading.value,
  )

  async function getUserById(id: string): Promise<void> {
    const result = await getRequest.execute(id)
    if (!result) return

    profile.value = result
    firstName.value = result.firstName ?? ''
    /* Бэкенд возвращает пробелы вместо пустых строк — в поле они не нужны */
    lastName.value = result.lastName?.trim() ?? ''
    email.value = result.email?.trim() ?? ''
    gender.value = result.genderId
    // Бэк отдаёт телефон целиком, в форме храним без кода страны
    userPhone.value = result.phone1?.substring(3) ?? ''
    if (result.birthDate) birthDate.value = result.birthDate
    setUserId(result.id ?? id)
  }

  async function createUser(): Promise<void> {
    const result = await createRequest.execute(user.value)
    if (!result?.customerId) return
    await getUserById(result.customerId)
  }

  async function updateUser(): Promise<boolean> {
    const result = await updateRequest.execute(user.value)
    if (!result?.success) return false

    /* Фото уехало — дальше показываем то, что вернул бэкенд */
    photo.value = ''
    await getUserById(userId.value)
    return true
  }

  /**
   * Выход: забываем клиента на этом устройстве — профиль, язык и всё, что
   * считается от аккаунта. Корзину, избранное и точку доставки убирают свои
   * сторы: они следят за сменой `userId`.
   *
   * Язык сбрасываем здесь же: следующий человек за этим телефоном должен
   * выбрать свой, а не читать интерфейс на чужом.
   */
  function logOut(): void {
    setCookie(LOGGED_OUT_COOKIE, '1')
    resetLocale()
    firstName.value = ''
    lastName.value = ''
    email.value = ''
    gender.value = null
    photo.value = ''
    userPhone.value = ''
    birthDate.value = ''
    profile.value = null
    setUserId('')
  }

  return {
    user,
    userId,
    isAuthorized,
    isProfileLoaded,
    addressList,
    isLoading,
    scoreRate,
    firstName,
    lastName,
    email,
    gender,
    photo,
    avatarUrl,
    isFirstNameCorrect,
    code,
    userPhone,
    isPhoneCorrect,
    userPhoneNumber,
    birthDate,
    updateError: updateRequest.error,
    getUserById,
    createUser,
    updateUser,
    logOut,
    setUserId,
  }
})
